import { useEffect, useState, type FormEvent } from 'react';
import { Check, Crown, Gift, LoaderCircle, LockKeyhole, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import type { MembershipPlan } from '../types';
import { useExperience } from '../content/ExperienceContext';

type BillingCycle = 'monthly' | 'yearly';

function priceText(cents: number) {
  return `¥${(cents / 100).toFixed(2)}`;
}

export function MembershipModal({ onClose }: { onClose: () => void }) {
  const { user, redeemCode } = useAuth();
  const { locale } = useExperience();
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [cycle, setCycle] = useState<BillingCycle>('yearly');
  const [notice, setNotice] = useState('在线支付正在准备中，当前不会扣款。');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState<{ text: string; error: boolean } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/membership/plans${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`, { credentials: 'include', signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || '会员方案读取失败');
        setPlans(data.plans || []);
        setNotice(data.paymentNotice || '在线支付正在准备中，当前由管理员开通。');
      })
      .catch((reason) => { if (reason.name !== 'AbortError') setError(reason.message || '会员方案读取失败'); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [locale]);

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (!code.trim() || redeeming || user?.tier === 'lifetime') return;
    setRedeeming(true);
    setRedeemResult(null);
    try {
      const result = await redeemCode(code);
      const date = new Date(result.professionalExpiresAt).toLocaleDateString('zh-CN');
      setRedeemResult({ text: `兑换成功，增加 ${result.durationDays} 天专业版，有效期累计至 ${date}`, error: false });
      setCode('');
    } catch (reason) {
      setRedeemResult({ text: reason instanceof Error ? reason.message : '兑换失败，请稍后再试', error: true });
    } finally { setRedeeming(false); }
  }

  return (
    <div className="modal-backdrop membership-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="membership-modal" role="dialog" aria-modal="true" aria-labelledby="membership-title">
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="关闭"><X size={20} /></button>
        <div className="membership-heading"><span className="membership-icon"><Crown size={24} /></span><div><p className="eyebrow">MEMBERSHIP</p><h2 id="membership-title">选择适合你的版本</h2><p>免费体验、限时专业、一次买断或持续订阅，权益各有侧重。</p></div></div>
        <form className="redeem-box" onSubmit={submitCode}>
          <span className="redeem-icon"><Gift size={19} /></span>
          <label htmlFor="membership-code"><strong>有兑换码？</strong><small>{user?.tier === 'lifetime' ? '买断版已经永久有效，无需兑换' : '输入 CDK，免费领取专业版天数'}</small></label>
          <div className="redeem-input"><input id="membership-code" value={code} onChange={(event) => { setCode(event.target.value.toUpperCase()); setRedeemResult(null); }} placeholder="YXMPD-XXXX-XXXX-XXXX" maxLength={32} autoComplete="off" disabled={user?.tier === 'lifetime'} /><button type="submit" disabled={!code.trim() || redeeming || user?.tier === 'lifetime'}>{redeeming ? <><LoaderCircle className="spin" size={15} />兑换中</> : '立即兑换'}</button></div>
          {redeemResult && <p className={redeemResult.error ? 'redeem-message error' : 'redeem-message success'} role="status">{redeemResult.text}</p>}
        </form>
        <div className="billing-tabs" aria-label="计费周期"><button className={cycle === 'monthly' ? 'active' : ''} type="button" onClick={() => setCycle('monthly')}>按月</button><button className={cycle === 'yearly' ? 'active' : ''} type="button" onClick={() => setCycle('yearly')}>按年 <small>更划算</small></button></div>
        {loading && <div className="membership-loading">正在读取方案…</div>}
        {error && <div className="membership-loading error">{error}</div>}
        {!loading && !error && <div className="plan-grid">{plans.map((plan) => {
          const current = user?.tier === plan.tier;
          const subscriptionCents = cycle === 'monthly' ? plan.monthlyPriceCents : plan.yearlyPriceCents;
          const discountedCents = Math.round(plan.oneTimePriceCents * (100 - plan.discountPercent) / 100);
          return <article className={`plan-card ${plan.tier === 'subscription' ? 'featured' : ''} plan-${plan.tier}`} key={plan.tier}>
            <div className="plan-card-head"><div><span>{plan.badge}</span><h3>{plan.name}</h3></div>{current && <b>当前方案</b>}</div>
            <p>{plan.summary}</p>
            <div className="plan-offer">
              {plan.tier === 'free' && <div className="plan-price"><strong>免费</strong></div>}
              {plan.tier === 'pro' && <div className="plan-price plan-issued"><strong>限时使用</strong><span>支持兑换码领取 · 默认 {plan.defaultDurationDays || 30} 天</span></div>}
              {plan.tier === 'lifetime' && <div className="plan-price lifetime-price"><strong>{priceText(discountedCents)}</strong><span>/永久</span>{plan.discountPercent > 0 && <><del>{priceText(plan.oneTimePriceCents)}</del><em>{plan.discountLabel || `${plan.discountPercent}% 优惠`}</em></>}</div>}
              {plan.tier === 'subscription' && <div className="plan-price"><strong>{priceText(subscriptionCents)}</strong><span>/{cycle === 'monthly' ? '月' : '年'}</span></div>}
              {current && user?.expiresAt && <small className="current-expiry">有效至 {new Date(user.expiresAt).toLocaleDateString('zh-CN')}</small>}
            </div>
            <ul>{plan.benefits.map((benefit) => <li key={benefit}><Check size={16} />{benefit}</li>)}</ul>
            <button type="button" disabled>{current ? '正在使用' : plan.tier === 'pro' ? '使用兑换码开通' : plan.tier === 'free' ? '注册即可使用' : '支付暂未开放'}</button>
          </article>;
        })}</div>}
        <div className="payment-notice"><LockKeyhole size={17} /><span>{notice}</span></div>
      </section>
    </div>
  );
}
